import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  CalendarCheck2,
  CheckCircle2,
  CreditCard,
  MessageCircle,
  School,
  ShieldCheck,
  Sparkles,
  UsersRound,
  Video,
} from 'lucide-react';
import { PublicLayout } from '../components/layout';
import Button from '../components/ui/Button';
import Surface from '../components/ui/Surface';
import ClassTechLogo from '../components/brand/ClassTechLogo';

const audiences = ['المدرسون', 'السناتر', 'المدارس', 'الأكاديميات'];

const features = [
  {
    icon: UsersRound,
    title: 'إدارة الطلاب',
    description: 'ملفات منظمة، أكواد ربط، ومتابعة واضحة لكل طالب وولي أمر.',
  },
  {
    icon: CalendarCheck2,
    title: 'الحصص والحضور',
    description: 'جدولة مرنة وسجل حضور يساعدك على متابعة الالتزام بسهولة.',
  },
  {
    icon: CreditCard,
    title: 'المدفوعات',
    description: 'تتبع المدفوعات والحالات المكتملة والمتأخرة من نفس اللوحة.',
  },
  {
    icon: BarChart3,
    title: 'التقارير',
    description: 'مؤشرات أداء وتعليم واضحة لدعم قرارات أفضل.',
  },
];

const modules = [
  'الطلاب وأولياء الأمور',
  'الحصص والجداول',
  'الواجبات والدرجات',
  'الحضور',
  'المدفوعات',
  'الموارد البشرية',
  'الإشعارات',
  'التقارير',
];

const plans = [
  { name: 'Starter', price: 'للمدرسين', description: 'إدارة أساسية للطلاب والحصص.' },
  { name: 'Growth', price: 'للسناتر', description: 'تشغيل أوسع مع تقارير ومدفوعات.' },
  { name: 'Institution', price: 'للمدارس', description: 'صلاحيات وأدوار وهيكلة مؤسسية.' },
];

const faqs = [
  {
    question: 'هل تدعم المنصة المدرس الفردي والمؤسسات؟',
    answer: 'نعم، صممت Class Tech لتناسب المدرسين والسناتر والمدارس والأكاديميات.',
  },
  {
    question: 'هل تتغير صلاحيات المستخدم حسب الدور؟',
    answer: 'نعم، كل مستخدم يرى المساحة المناسبة لدوره وصلاحياته الحالية.',
  },
  {
    question: 'هل يمكن استخدام Zoom وWhatsApp ووسائل الدفع؟',
    answer: 'تدعم المنصة التكاملات التعليمية والتواصلية والمالية ضمن تجربة واحدة.',
  },
];

const LandingPage: React.FC = () => {
  return (
    <PublicLayout>
      <section className='bg-white dark:bg-secondary-950'>
        <div className='mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1fr_0.9fr] lg:px-8'>
          <div>
            <ClassTechLogo size='lg' showTagline />
            <h1 className='mt-10 max-w-3xl text-4xl font-bold leading-tight text-brand-navy dark:text-white md:text-6xl'>
              إدارة تعليمية متكاملة من مكان واحد
            </h1>
            <p className='mt-6 max-w-2xl text-lg leading-8 text-brand-text-secondary dark:text-secondary-300'>
              أدِر الطلاب والحصص والحضور والواجبات والمدفوعات والتقارير بسهولة
              من خلال منصة Class Tech.
            </p>
            <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
              <Link to='/register'>
                <Button size='lg' className='w-full sm:w-auto'>
                  ابدأ تجربتك
                </Button>
              </Link>
              <Link to='/features'>
                <Button variant='outline' size='lg' className='w-full sm:w-auto'>
                  استكشف المميزات
                </Button>
              </Link>
            </div>
          </div>

          <div className='rounded-lg border border-secondary-200 bg-brand-background p-4 shadow-surface dark:border-secondary-800 dark:bg-secondary-900'>
            <div className='rounded-lg bg-white p-5 shadow-surface dark:bg-secondary-950'>
              <div className='flex items-center justify-between border-b border-secondary-100 pb-4 dark:border-secondary-800'>
                <div>
                  <p className='text-sm font-semibold text-brand-text-secondary dark:text-secondary-400'>
                    لوحة اليوم
                  </p>
                  <h2 className='mt-1 text-xl font-bold text-brand-navy dark:text-white'>
                    متابعة التشغيل التعليمي
                  </h2>
                </div>
                <span className='rounded-lg bg-primary-50 p-3 text-primary-700'>
                  <BarChart3 className='h-6 w-6' />
                </span>
              </div>
              <div className='mt-5 grid grid-cols-2 gap-3'>
                {[
                  ['إجمالي الطلاب', '248'],
                  ['الحصص اليوم', '18'],
                  ['نسبة الحضور', '92%'],
                  ['مدفوعات مكتملة', 'EGP 42K'],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className='rounded-lg border border-secondary-100 bg-white p-4 dark:border-secondary-800 dark:bg-secondary-900'
                  >
                    <p className='text-xs font-semibold text-brand-text-secondary dark:text-secondary-400'>
                      {label}
                    </p>
                    <p className='mt-2 font-english text-2xl font-bold text-brand-navy dark:text-white'>
                      {value}
                    </p>
                  </div>
                ))}
              </div>
              <div className='mt-5 space-y-3'>
                {['حضور الصف الثالث', 'دفعة مدفوعة', 'واجب جديد'].map(
                  (label, index) => (
                    <div
                      key={label}
                      className='flex items-center gap-3 rounded-lg bg-brand-background p-3 dark:bg-secondary-900'
                    >
                      <CheckCircle2
                        className={
                          index === 1
                            ? 'h-5 w-5 text-success-600'
                            : 'h-5 w-5 text-primary-600'
                        }
                      />
                      <span className='text-sm font-semibold text-brand-navy dark:text-white'>
                        {label}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='py-16 dark:bg-secondary-950'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <h2 className='text-3xl font-bold text-brand-navy dark:text-white'>لمن تناسب المنصة</h2>
          <div className='mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
            {audiences.map(item => (
              <Surface key={item} className='flex items-center gap-3'>
                <School className='h-6 w-6 text-primary-600' />
                <span className='font-semibold text-brand-navy dark:text-white'>{item}</span>
              </Surface>
            ))}
          </div>
        </div>
      </section>

      <section className='bg-white py-16 dark:bg-secondary-950'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='flex flex-col justify-between gap-4 md:flex-row md:items-end'>
            <div>
              <h2 className='text-3xl font-bold text-brand-navy dark:text-white'>أهم المميزات</h2>
              <p className='mt-3 max-w-2xl text-brand-text-secondary dark:text-secondary-300'>
                أدوات يومية واضحة تساعدك على إدارة التعليم بدون ازدحام أو تعقيد.
              </p>
            </div>
            <Link to='/features' className='font-semibold text-primary-700'>
              عرض التفاصيل
            </Link>
          </div>
          <div className='mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4'>
            {features.map(feature => {
              const Icon = feature.icon;
              return (
                <Surface key={feature.title}>
                  <Icon className='h-7 w-7 text-primary-600' />
                  <h3 className='mt-5 text-lg font-bold text-brand-navy dark:text-white'>
                    {feature.title}
                  </h3>
                  <p className='mt-3 text-sm leading-6 text-brand-text-secondary dark:text-secondary-300'>
                    {feature.description}
                  </p>
                </Surface>
              );
            })}
          </div>
        </div>
      </section>

      <section className='py-16 dark:bg-secondary-950'>
        <div className='mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-2 lg:px-8'>
          <div>
            <h2 className='text-3xl font-bold text-brand-navy dark:text-white'>وحدات النظام</h2>
            <div className='mt-6 grid gap-3 sm:grid-cols-2'>
              {modules.map(module => (
                <div
                  key={module}
                  className='flex items-center gap-3 rounded-lg border border-secondary-200 bg-white p-3 dark:border-secondary-800 dark:bg-secondary-900'
                >
                  <span className='h-2.5 w-2.5 rounded-full bg-brand-cyan' />
                  <span className='text-sm font-semibold text-brand-navy dark:text-white'>
                    {module}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <Surface padding='lg'>
            <h2 className='text-2xl font-bold text-brand-navy dark:text-white'>طريقة العمل</h2>
            <div className='mt-6 space-y-4'>
              {['أنشئ حسابك وحدد دورك', 'أضف الطلاب والحصص', 'تابع الأداء والمدفوعات من التقارير'].map(
                (step, index) => (
                  <div key={step} className='flex gap-4'>
                    <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-600 font-english font-bold text-white'>
                      {index + 1}
                    </span>
                    <p className='font-semibold text-brand-navy dark:text-white'>{step}</p>
                  </div>
                )
              )}
            </div>
          </Surface>
        </div>
      </section>

      <section className='bg-white py-16 dark:bg-secondary-950'>
        <div className='mx-auto grid max-w-7xl gap-5 px-4 sm:px-6 lg:grid-cols-3 lg:px-8'>
          {[
            { icon: Video, title: 'تكامل Zoom', text: 'تنظيم الحصص الأونلاين ومتابعة الحضور.' },
            { icon: MessageCircle, title: 'WhatsApp', text: 'تواصل أوضح مع الطلاب وأولياء الأمور.' },
            { icon: CreditCard, title: 'وسائل الدفع', text: 'تتبع حالات الدفع والتحصيل بسهولة.' },
          ].map(item => {
            const Icon = item.icon;
            return (
              <Surface key={item.title}>
                <Icon className='h-7 w-7 text-brand-cyan' />
                <h3 className='mt-4 text-lg font-bold text-brand-navy dark:text-white'>
                  {item.title}
                </h3>
                <p className='mt-2 text-sm leading-6 text-brand-text-secondary dark:text-secondary-300'>
                  {item.text}
                </p>
              </Surface>
            );
          })}
        </div>
      </section>

      <section className='py-16 dark:bg-secondary-950'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='grid gap-5 lg:grid-cols-3'>
            {plans.map(plan => (
              <Surface key={plan.name} padding='lg'>
                <p className='font-english text-sm font-bold text-primary-700'>
                  {plan.name}
                </p>
                <h3 className='mt-3 text-2xl font-bold text-brand-navy dark:text-white'>
                  {plan.price}
                </h3>
                <p className='mt-3 text-sm leading-6 text-brand-text-secondary dark:text-secondary-300'>
                  {plan.description}
                </p>
              </Surface>
            ))}
          </div>
        </div>
      </section>

      <section className='bg-white py-16 dark:bg-secondary-950'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='grid gap-8 lg:grid-cols-[0.8fr_1fr]'>
            <div>
              <Sparkles className='h-8 w-8 text-brand-cyan' />
              <h2 className='mt-4 text-3xl font-bold text-brand-navy dark:text-white'>
                لماذا Class Tech
              </h2>
              <p className='mt-3 leading-7 text-brand-text-secondary dark:text-secondary-300'>
                لأنها تجمع التشغيل اليومي، التواصل، والتحليل في تجربة منظمة
                وسهلة الاستخدام لكل الأدوار.
              </p>
            </div>
            <div className='space-y-4'>
              {faqs.map(item => (
                <Surface key={item.question}>
                  <h3 className='font-bold text-brand-navy dark:text-white'>{item.question}</h3>
                  <p className='mt-2 text-sm leading-6 text-brand-text-secondary dark:text-secondary-300'>
                    {item.answer}
                  </p>
                </Surface>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='bg-sidebar py-16 text-white'>
        <div className='mx-auto flex max-w-7xl flex-col gap-6 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8'>
          <div>
            <ShieldCheck className='h-8 w-8 text-brand-cyan' />
            <h2 className='mt-4 text-3xl font-bold'>ابدأ إدارة التعليم بثقة</h2>
            <p className='mt-3 max-w-2xl text-secondary-300'>
              منصة واحدة للطلاب والحصص والحضور والواجبات والمدفوعات والتقارير.
            </p>
          </div>
          <Link to='/register'>
            <Button size='lg' className='bg-white text-primary-700 hover:bg-primary-50'>
              إنشاء حساب
            </Button>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
};

export default LandingPage;
